import { memo, useCallback, useMemo, type MouseEvent } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  type Node as RFNode,
  type Edge,
  type EdgeTypes,
  type NodeTypes,
  type NodeProps,
} from '@xyflow/react';
import { makeStyles, tokens, Spinner, Text } from '@fluentui/react-components';
import {
  EditRegular,
  WarningRegular,
  DismissRegular,
  FlashFilled,
  PlayFilled,
  WrenchRegular,
  BrainCircuitFilled,
  BotSparkleFilled,
  ArrowRepeatAllFilled,
  BranchForkFilled,
  NavigationFilled,
} from '@fluentui/react-icons';
import { useSubWorkflowMetadata } from '@/hooks/useSubWorkflowMetadata';
import { convertWorkflowToGraph, type GraphNode } from '@/hooks/useWorkflowGraph';
import { FloatingNode, type NodeStatus } from '../FloatingNode';
import type { FloatingNodeProps } from '../FloatingNode';
import { DottedEdge } from '../../edges/DottedEdge';
import type { AgentTool } from '@/types/workflowDefinition';

// -- Node type → FloatingNode prop mapping --

type PreviewNodeData = {
  label: string;
  actionType: string;
  nodeType: string; // trigger, action, agent, tool, model, if, switch, foreach
  tools?: Record<string, AgentTool>;
};

type PreviewNodeRFType = RFNode<PreviewNodeData, 'preview'>;

const nodeTypeConfig: Record<string, {
  shape?: FloatingNodeProps['shape'];
  accentColor?: FloatingNodeProps['accentColor'];
  icon: React.ReactNode;
}> = {
  trigger: { shape: 'circle', accentColor: 'green', icon: <FlashFilled /> },
  action: { accentColor: 'brand', icon: <PlayFilled /> },
  agent: { shape: 'hexagon', accentColor: 'teal', icon: <BotSparkleFilled /> },
  tool: { accentColor: 'neutral', icon: <WrenchRegular /> },
  model: { accentColor: 'neutral', icon: <BrainCircuitFilled /> },
  if: { shape: 'diamond', accentColor: 'purple', icon: <BranchForkFilled /> },
  switch: { shape: 'diamond', accentColor: 'purple', icon: <NavigationFilled /> },
  foreach: { shape: 'diamond', accentColor: 'purple', icon: <ArrowRepeatAllFilled /> },
};

const defaultConfig = { shape: undefined as FloatingNodeProps['shape'], accentColor: 'neutral' as const, icon: <PlayFilled /> };

// Hoisted module-level handler: pure stopPropagation has no dependencies, so
// recreating it on every render of every preview just churns the GC.
const stopPropagationOnly = (e: MouseEvent) => e.stopPropagation();

/**
 * Preview node: wraps FloatingNode with type-appropriate shape/color/icon.
 * No store or context dependencies — purely visual.
 */
const PreviewNode = memo(({ data }: NodeProps<PreviewNodeRFType>) => {
  const config = nodeTypeConfig[data.nodeType] ?? defaultConfig;
  const isAgent = data.nodeType === 'agent';

  return (
    <FloatingNode
      icon={config.icon}
      label={data.label}
      {...(config.shape ? { shape: config.shape } : {})}
      accentColor={config.accentColor ?? 'neutral'}
      detailLevel="compact"
      selected={false}
      status={'default' as NodeStatus}
      ariaLabel={`${data.nodeType}: ${data.label}`}
    >
      {/* Default handles for main flow edges */}
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
      {/* Agent-specific handles for tool/model edges */}
      {isAgent && (
        <>
          <Handle type="source" position={Position.Top} id="tools" style={{ opacity: 0 }} isConnectable={false} />
          <Handle type="source" position={Position.Bottom} id="model" style={{ opacity: 0 }} isConnectable={false} />
        </>
      )}
    </FloatingNode>
  );
});
PreviewNode.displayName = 'PreviewNode';

/** Tool preview node — target handle at bottom (matches tool edge targetHandle="input") */
const PreviewToolNode = memo(({ data }: NodeProps<PreviewNodeRFType>) => {
  return (
    <FloatingNode
      icon={<WrenchRegular />}
      label={data.label}
      accentColor="neutral"
      detailLevel="compact"
      selected={false}
      status={'default' as NodeStatus}
      ariaLabel={`Tool: ${data.label}`}
    >
      <Handle type="target" position={Position.Bottom} id="input" style={{ opacity: 0 }} isConnectable={false} />
    </FloatingNode>
  );
});
PreviewToolNode.displayName = 'PreviewToolNode';

/** Model preview node — target handle at top */
const PreviewModelNode = memo(({ data }: NodeProps<PreviewNodeRFType>) => {
  return (
    <FloatingNode
      icon={<BrainCircuitFilled />}
      label={data.label}
      accentColor="neutral"
      detailLevel="compact"
      selected={false}
      status={'default' as NodeStatus}
      ariaLabel={`Model: ${data.label}`}
    >
      <Handle type="target" position={Position.Top} id="input" style={{ opacity: 0 }} isConnectable={false} />
    </FloatingNode>
  );
});
PreviewModelNode.displayName = 'PreviewModelNode';

const previewNodeTypes: NodeTypes = {
  preview: PreviewNode,
  previewTool: PreviewToolNode,
  previewModel: PreviewModelNode,
};

const previewEdgeTypes: EdgeTypes = { dotted: DottedEdge };

// -- Styles --

const useStyles = makeStyles({
  container: {
    marginTop: tokens.spacingVerticalXS,
    border: `1.5px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusSmall,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  canvas: {},
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalL,
  },
  topBarActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
});

// -- Tool/model node generation (same logic as WorkflowDesigner.tsx) --

function generateToolAndModelNodes(graphNodes: GraphNode[]) {
  const toolNodes: RFNode[] = [];
  const toolEdges: Edge[] = [];
  const modelNodes: RFNode[] = [];
  const modelEdges: Edge[] = [];

  const agentNodeWidth = 60;
  const toolNodeHeight = 80;
  const toolNodeWidth = 120;
  const baseVerticalGap = 80;
  const staggerOffset = 30;
  const horizontalGap = 20;
  const modelNodeWidth = 100;
  const modelVerticalGap = 60;

  graphNodes.forEach((node) => {
    if (node.type !== 'agent') return;

    const agentName = node.data.actionName;
    const tools = node.data.tools;
    const agentModelType = (node.data as { agentModelType?: string }).agentModelType;
    const agentHarnessType = (node.data as { agentHarnessType?: string }).agentHarnessType;
    const isFoundryAgent =
      agentModelType === 'FoundryAgentService' || agentModelType === 'FoundryAgentServiceV2';
    const isCodingAgent = typeof agentHarnessType === 'string' && agentHarnessType.length > 0;

    // Generate tool nodes (positioned ABOVE agent — same as WorkflowDesigner)
    if (tools) {
      const toolEntries = Object.entries(tools);
      const toolCount = toolEntries.length;
      if (toolCount > 0) {
        const totalToolsWidth = toolCount * toolNodeWidth + (toolCount - 1) * horizontalGap;
        const relativeStartX = agentNodeWidth / 2 - totalToolsWidth / 2;
        const relativeBaseY = -(baseVerticalGap + toolNodeHeight);

        toolEntries.forEach(([toolName, tool], index) => {
          const toolActions = tool.actions ?? {};
          const firstActionEntry = Object.entries(toolActions)[0];
          const innerActionName = firstActionEntry?.[0] ?? toolName;
          const toolNodeId = `tool-${agentName}-${innerActionName}`;

          const toolX = relativeStartX + index * (toolNodeWidth + horizontalGap);
          const toolY = relativeBaseY - (index % 2 === 1 ? staggerOffset : 0);

          toolNodes.push({
            id: toolNodeId,
            type: 'previewTool',
            position: { x: toolX, y: toolY },
            parentId: node.id,
            data: { label: toolName, actionType: 'Tool', nodeType: 'tool' },
            draggable: false,
            selectable: false,
          });

          toolEdges.push({
            id: `${node.id}->${toolNodeId}`,
            source: node.id,
            target: toolNodeId,
            type: 'dotted',
            sourceHandle: 'tools',
            targetHandle: 'input',
            data: { agentVariant: isCodingAgent ? 'coding' : isFoundryAgent ? 'foundry' : 'native' },
          });
        });
      }
    }

    // Generate model node (positioned BELOW agent — same as WorkflowDesigner)
    const modelDeployment = node.data.modelDeployment as string | undefined;
    if (modelDeployment) {
      const modelX = (agentNodeWidth - modelNodeWidth) / 2;
      const modelY = 70 + modelVerticalGap;
      const modelNodeId = `model-${agentName}`;

      modelNodes.push({
        id: modelNodeId,
        type: 'previewModel',
        position: { x: modelX, y: modelY },
        parentId: node.id,
        data: {
          label: modelDeployment.split('/').pop() ?? 'Model',
          actionType: 'Model',
          nodeType: 'model',
        },
        draggable: false,
        selectable: false,
      });

      modelEdges.push({
        id: `${node.id}->model`,
        source: node.id,
        target: modelNodeId,
        type: 'dotted',
        sourceHandle: 'model',
        targetHandle: 'input',
        data: { agentVariant: isCodingAgent ? 'coding' : isFoundryAgent ? 'foundry' : 'native' },
      });
    }
  });

  return { toolNodes, toolEdges, modelNodes, modelEdges };
}

// -- Inline sub-workflow preview (scope-style, rendered inside tool node) --

export interface SubWorkflowPreviewProps {
  workflowName: string;
  onEditClick?: () => void;
  onClose?: () => void;
}

export const SubWorkflowPreview = memo(
  ({ workflowName, onEditClick, onClose }: SubWorkflowPreviewProps) => {
    const styles = useStyles();
    const { definition, stepCount, isLoading, isError } = useSubWorkflowMetadata(workflowName);

    // Same pipeline as WorkflowDesigner: convertWorkflowToGraph → tool/model generation → merge
    const { nodes, edges, width, height } = useMemo(() => {
      if (!definition) return { nodes: [] as RFNode[], edges: [] as Edge[], width: 280, height: 180 };

      const graph = convertWorkflowToGraph(definition, 'horizontal');

      // Map GraphNodes to preview nodes with type-appropriate rendering
      const previewNodes: RFNode[] = graph.nodes.map(n => ({
        id: n.id,
        type: 'preview',
        position: n.position,
        data: {
          label: n.data.label,
          actionType: n.data.actionType,
          nodeType: n.type ?? 'action',
          tools: n.data.tools,
        },
        draggable: false,
        selectable: false,
      }));

      // Generate tool + model nodes from agent nodes (same logic as WorkflowDesigner)
      const { toolNodes, toolEdges, modelNodes, modelEdges } = generateToolAndModelNodes(graph.nodes);

      // Merge all nodes and edges
      const allNodes = [...previewNodes, ...toolNodes, ...modelNodes];
      const allEdges = [...graph.edges, ...toolEdges, ...modelEdges];

      // Calculate container size from all node positions
      const allPositions = allNodes
        .filter(n => !n.parentId) // Only root-level nodes for sizing
        .map(n => n.position);
      const maxX = Math.max(0, ...allPositions.map(p => p.x));
      const maxY = Math.max(0, ...allPositions.map(p => p.y));

      return {
        nodes: allNodes,
        edges: allEdges,
        width: Math.min(800, Math.max(280, maxX + 250)),
        height: Math.min(500, Math.max(200, maxY + 200)),
      };
    }, [definition]);

    const handleEditClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        onEditClick?.();
      },
      [onEditClick],
    );

    const handleClose = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        onClose?.();
      },
      [onClose],
    );

    const renderContent = () => {
      if (isLoading) {
        return (
          <div className={styles.centered}>
            <Spinner size="small" label="Loading workflow..." />
          </div>
        );
      }
      if (isError) {
        return (
          <div className={styles.centered}>
            <WarningRegular fontSize={20} />
            <Text size={200}>Unable to load sub-workflow</Text>
          </div>
        );
      }
      if (stepCount === 0) {
        return (
          <div className={styles.centered}>
            <Text size={200}>This sub-workflow has no actions</Text>
          </div>
        );
      }
      return (
        <div className={styles.canvas} style={{ width, height }}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={previewNodeTypes}
              edgeTypes={previewEdgeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              zoomOnPinch
              preventScrolling
              minZoom={0.2}
              maxZoom={1.5}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              proOptions={{ hideAttribution: true }}
            />
          </ReactFlowProvider>
        </div>
      );
    };

    return (
      <div className={styles.container} onClick={stopPropagationOnly}>
        <div className={styles.topBar}>
          <Text size={200} weight="semibold">{workflowName}</Text>
          <div className={styles.topBarActions}>
            <button
              type="button"
              className={styles.editButton}
              onClick={handleEditClick}
              aria-label={`Open sub-workflow ${workflowName}`}
              title="Open workflow"
            >
              <EditRegular fontSize={16} />
            </button>
            <button
              type="button"
              className={styles.editButton}
              onClick={handleClose}
              aria-label="Close preview"
              title="Close"
            >
              <DismissRegular fontSize={16} />
            </button>
          </div>
        </div>
        {renderContent()}
      </div>
    );
  }
);

SubWorkflowPreview.displayName = 'SubWorkflowPreview';
